import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { Platform, Text, View } from 'react-native';
import { auth, db, onAuthStateChanged } from '../firebaseConfig';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

const NavigationContext = createContext();

// Global flags to prevent duplicate listener creation across hot-reloads
if (typeof window !== 'undefined') {
  window.__firestoreListeners = window.__firestoreListeners || {
    userListener: null,
    maintenanceListener: null,
    authListener: null,
  };
}

// Screen to URL mapping
const screenToUrl = {
  'MainPage': '/coming-soon',
  'Home': '/home',
  'Login': '/login',
  'Register': '/register',
  'Dashboard': '/dashboard',
  'Explore': '/explore',
  'SameGameParlay': '/optimizer',
  'SgpFlow': '/results',
  'Profile': '/profile',
  'ForgotPassword': '/forgotpassword',
  'Admin': '/admin',
  'Plans': '/plans',
  'Success': '/subscription-success',
  'Suspended': '/suspended',
};

// URL to screen mapping
const urlToScreen = {
  '/': 'Home',
  '/home': 'Home',
  '/login': 'Login',
  '/register': 'Register',
  '/dashboard': 'Dashboard',
  '/explore': 'Explore',
  '/optimizer': 'SameGameParlay',
  '/results': 'SgpFlow',
  '/optimizer/results': 'SgpFlow',
  '/sgp': 'SameGameParlay', // Redirect old URL
  '/sgpflow': 'SgpFlow', // Redirect old URL
  '/optimizer/flow': 'SgpFlow', // Redirect old URL
  '/profile': 'Profile',
  '/forgotpassword': 'ForgotPassword',
  '/coming-soon': 'MainPage',
  '/admin': 'Admin',
  '/admin-login': 'AdminLogin',
  '/plans': 'Plans',
  '/subscription-success': 'Success',
  '/suspended': 'Suspended',
};

// Helper function to build URL with query parameters
const buildUrlWithParams = (screenName, payload, fromScreen) => {
  let baseUrl = screenToUrl[screenName] || '/';
  
  // For SgpFlow, use different URL based on where we're coming from
  if (screenName === 'SgpFlow') {
    if (fromScreen === 'SameGameParlay') {
      baseUrl = '/optimizer/results';
    } else {
      baseUrl = '/results';
    }
    
    // Add query parameter if query exists
    if (payload?.query) {
      const queryParam = encodeURIComponent(payload.query);
      return `${baseUrl}?q=${queryParam}`;
    }
  }
  
  return baseUrl;
};

export function NavigationProvider({ children }) {
  const [currentScreen, setCurrentScreen] = useState('Home');
  const [history, setHistory] = useState(['Home']);
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(null);
  const [routePayload, setRoutePayload] = useState(null);
  
  // Admin and maintenance state
  const [authUser, setAuthUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [alphaAccess, setAlphaAccess] = useState(false);
  const [comingSoonEnabled, setComingSoonEnabled] = useState(false);
  const [bannerEnabled, setBannerEnabled] = useState(false);
  const [bannerStartedAt, setBannerStartedAt] = useState(null);
  const [communityModalEnabled, setCommunityModalEnabled] = useState(true);
  const [communityRedirectUrl, setCommunityRedirectUrl] = useState('https://discord.gg/your-discord-link');

  // Calculate admin status
  const isAdmin = userType === 'admin';
  // const isAdmin = true;
  
  // Check if user has access when coming soon is enabled
  const hasComingSoonAccess = isAdmin || alphaAccess;



  // Get route from location helper
  const getRouteFromLocation = () => {
    if (Platform.OS !== 'web') return '/home';
    try {
      const path = window?.location?.pathname || '/home';
      // hash fallback support (e.g., /#/login)
      const hash = window?.location?.hash || '';
      if (hash.startsWith('#/')) return hash.slice(1);
      return path;
    } catch (e) {
      return '/home';
    }
  };

  // Initialize screen from URL on web
  useEffect(() => {
    if (Platform.OS === 'web') {
      const path = getRouteFromLocation();
      const screen = urlToScreen[path] || 'Home';
      // Guard: SgpFlow requires a query from payload or session
      const sgpQueryAvailable = (() => {
        try { return !!window.sessionStorage.getItem('sgp:lastQuery'); } catch { return false; }
      })();
      
      // Protect admin page on direct URL access (only check after user data is loaded)
      if (screen === 'Admin' && userDataLoaded && !isAdmin) {
        console.log('Access denied: Admin page requires admin privileges');
        setCurrentScreen('AdminLogin');
        setHistory(['AdminLogin']);
        // Update URL to home
        window.history.replaceState({}, '', '/admin-login');
      } else if (screen === 'Admin' && !userDataLoaded) {
        // Wait for user data to load before deciding
        console.log('Waiting for user data to load before admin check...');
      } else if (screen === 'SgpFlow' && !sgpQueryAvailable) {
        // If no saved query, send back to Home
        console.log('SgpFlow requires a query. Redirecting to Home.');
        setCurrentScreen('Home');
        setHistory(['Home']);
        setRoutePayload(null);
        window.history.replaceState({}, '', '/home');
      } else {
        setCurrentScreen(screen);
        setHistory([screen]);
        setRoutePayload(null);
      }
    }
  }, [isAdmin]);

  // Set document title based on current screen
  useEffect(() => {
    if (Platform.OS === 'web') {
      const screenTitles = {
        'Home': 'Home',
        'Login': 'Sign In',
        'Register': 'Register',
        'Explore': 'Explore',
        'SameGameParlay': 'Optimizer',
        'SgpFlow': 'Results',
        'Profile': 'Profile',
        'Plans': 'Plans',
        'Success': 'Subscription Success',
        'Admin': 'Admin',
        'AdminLogin': 'Admin Login',
        'ForgotPassword': 'Forgot Password',
        'MainPage': 'Coming Soon'
      };
      
      console.log('Setting title for currentScreen:', currentScreen);
      const title = screenTitles[currentScreen] || 'Bet Optimized';
      console.log('Setting document title to:', title);
      
      // Ensure title is never undefined or null
      if (title && typeof title === 'string' && title.trim() !== '') {
        document.title = title;
      } else {
        document.title = 'Bet Optimized';
      }
    }
  }, [currentScreen]);

  // Set initial title on mount
  useEffect(() => {
    if (Platform.OS === 'web') {
      console.log('Setting initial title');
      document.title = 'Bet Optimized';
    }
  }, []);

  // Listen for browser navigation events
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const onPop = () => {
      const path = getRouteFromLocation();
      const screen = urlToScreen[path] || 'Home';
      const sgpQueryAvailable = (() => {
        try { return !!window.sessionStorage.getItem('sgp:lastQuery'); } catch { return false; }
      })();
      
      // Protect admin page on browser navigation (only check after user data is loaded)
      if (screen === 'Admin' && userDataLoaded && !isAdmin) {
        console.log('Access denied: Admin page requires admin privileges');
        setCurrentScreen('Home');
        window.history.replaceState({}, '', '/home');
      } else if (screen === 'Admin' && !userDataLoaded) {
        // Wait for user data to load
        console.log('Waiting for user data to load before admin check...');
        return;
      } else if (screen === 'SgpFlow' && !sgpQueryAvailable) {
        console.log('SgpFlow requires a query. Redirecting to Home.');
        setCurrentScreen('Home');
        window.history.replaceState({}, '', '/home');
      } else {
        setCurrentScreen(screen);
        setRoutePayload(null);
      }
    };
    const onNavigate = () => {
      const path = getRouteFromLocation();
      const screen = urlToScreen[path] || 'Home';
      const sgpQueryAvailable = (() => {
        try { return !!window.sessionStorage.getItem('sgp:lastQuery'); } catch { return false; }
      })();
      
      // Protect admin page on browser navigation (only check after user data is loaded)
      if (screen === 'Admin' && userDataLoaded && !isAdmin) {
        console.log('Access denied: Admin page requires admin privileges');
        setCurrentScreen('Home');
        window.history.replaceState({}, '', '/home');
      } else if (screen === 'Admin' && !userDataLoaded) {
        // Wait for user data to load
        console.log('Waiting for user data to load before admin check...');
        return;
      } else if (screen === 'SgpFlow' && !sgpQueryAvailable) {
        console.log('SgpFlow requires a query. Redirecting to Home.');
        setCurrentScreen('Home');
        window.history.replaceState({}, '', '/home');
      } else {
        setCurrentScreen(screen);
      }
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('app:navigate', onNavigate);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('app:navigate', onNavigate);
    };
  }, [isAdmin]);

  // Auth listener (sets auth user). Role is handled by realtime snapshot below
  useEffect(() => {
    // Clean up any existing listener first
    if (typeof window !== 'undefined' && window.__firestoreListeners.authListener) {
      console.log('🧹 Cleaning up existing auth listener before creating new one');
      try {
        window.__firestoreListeners.authListener();
        window.__firestoreListeners.authListener = null;
      } catch (error) {
        console.warn('Error cleaning up auth listener:', error);
      }
    }
    
    let unsubscribed = false;
    console.log('🔍 Creating new auth listener');
    const unsub = onAuthStateChanged(auth, (u) => {
      if (unsubscribed) return;
      setAuthUser(u || null);
      if (!u) {
        setUserType(null);
      }
    });
    
    // Store unsub function globally
    if (typeof window !== 'undefined') {
      window.__firestoreListeners.authListener = unsub;
    }
    
    return () => {
      unsubscribed = true;
      if (unsub) {
        unsub();
      }
      // Clear global reference
      if (typeof window !== 'undefined') {
        window.__firestoreListeners.authListener = null;
      }
    };
  }, []);

  // Track if user data has been loaded (to prevent race conditions)
  const [userDataLoaded, setUserDataLoaded] = useState(false);

  // Realtime userType and alphaAccess listener so admin rights apply immediately after profile doc appears/changes
  useEffect(() => {
    if (!authUser?.uid) {
      setUserType(null);
      setAlphaAccess(false);
      setUserDataLoaded(false);
      // Clean up existing listener if user logged out
      if (typeof window !== 'undefined' && window.__firestoreListeners.userListener) {
        try {
          window.__firestoreListeners.userListener();
          window.__firestoreListeners.userListener = null;
        } catch (error) {
          console.error('❌ Error cleaning up user listener:', error);
        }
      }
      return;
    }
    
    // Clean up any existing listener first
    if (typeof window !== 'undefined' && window.__firestoreListeners.userListener) {
      console.log('🧹 Cleaning up existing user listener before creating new one');
      try {
        window.__firestoreListeners.userListener();
        window.__firestoreListeners.userListener = null;
      } catch (error) {
        console.warn('Error cleaning up user listener:', error);
      }
    }
    
    let unsubscribed = false;
    let unsub = null;
    let cleanupCalled = false;
    
    console.log('🔍 Creating new user listener for UID:', authUser.uid);
    
    // Use a short delay to debounce rapid re-renders and prevent duplicate listeners
    const timeoutId = setTimeout(() => {
      if (unsubscribed || cleanupCalled) return;
      
      const ref = doc(db, 'users', authUser.uid);
      
      try {
        unsub = onSnapshot(ref, (snap) => {
          if (unsubscribed || cleanupCalled) return;
          
          if (!snap.exists()) {
            setUserType('user');
            setAlphaAccess(false);
            setUserDataLoaded(true); // User data loaded (defaults)
            return;
          }
          
          const data = snap.data();
          const fetchedUserType = data.userType || 'user';
          const fetchedAlphaAccess = !!data.alphaAccess;
          
          setUserType(fetchedUserType);
          setAlphaAccess(fetchedAlphaAccess);
          setUserDataLoaded(true); // User data loaded successfully
        }, (error) => {
          if (unsubscribed || cleanupCalled) return;
          console.error('❌ [NavigationContext] Error loading user document:', error);
          setUserType('user');
          setAlphaAccess(false);
          setUserDataLoaded(true); // Mark as loaded even on error (fail-safe)
        });
        
        // Store unsub function globally
        if (typeof window !== 'undefined') {
          window.__firestoreListeners.userListener = unsub;
        }
      } catch (error) {
        console.error('❌ [NavigationContext] Error setting up listener:', error);
      }
    }, 100); // 100ms debounce
    
    return () => {
      unsubscribed = true;
      cleanupCalled = true;
      clearTimeout(timeoutId);
      if (unsub) {
        try {
          unsub();
        } catch (error) {
          console.error('❌ [NavigationContext] Error unsubscribing:', error);
        }
      }
      // Clear global reference
      if (typeof window !== 'undefined') {
        window.__firestoreListeners.userListener = null;
      }
    };
  }, [authUser?.uid]);

  // Maintenance listener
  useEffect(() => {
    // Clean up any existing listener first
    if (typeof window !== 'undefined' && window.__firestoreListeners.maintenanceListener) {
      console.log('🧹 Cleaning up existing maintenance listener before creating new one');
      try {
        window.__firestoreListeners.maintenanceListener();
        window.__firestoreListeners.maintenanceListener = null;
      } catch (error) {
        console.warn('Error cleaning up maintenance listener:', error);
      }
    }
    
    let unsubscribed = false;
    let unsub = null;
    let cleanupCalled = false;
    
    console.log('🔍 Creating new maintenance listener');
    
    // Debounce to prevent duplicate listeners during hot-reloads
    const timeoutId = setTimeout(() => {
      if (unsubscribed || cleanupCalled) return;
      
      const ref = doc(db, 'site_maintenance', 'global');
      
      try {
        unsub = onSnapshot(ref, (snap) => {
          if (unsubscribed || cleanupCalled) return;
          
          const d = snap.data() || {};
          setComingSoonEnabled(!!d.comingSoonEnabled);
          setBannerEnabled(!!d.maintenanceBannerEnabled);
          setBannerStartedAt(d.maintenanceBannerStartedAt || null);
          setCommunityModalEnabled(d.communityModalEnabled !== false); // Default to true
          setCommunityRedirectUrl(d.communityRedirectUrl || 'https://discord.gg/your-discord-link');
        });
        
        // Store unsub function globally
        if (typeof window !== 'undefined') {
          window.__firestoreListeners.maintenanceListener = unsub;
        }
      } catch (error) {
        console.error('❌ [NavigationContext] Error setting up maintenance listener:', error);
      }
    }, 100);
    
    return () => {
      unsubscribed = true;
      cleanupCalled = true;
      clearTimeout(timeoutId);
      if (unsub) {
        try {
          unsub();
        } catch (error) {
          console.error('❌ [NavigationContext] Error unsubscribing from maintenance:', error);
        }
      }
      // Clear global reference
      if (typeof window !== 'undefined') {
        window.__firestoreListeners.maintenanceListener = null;
      }
    };
  }, []);

  // Prod host detection
  const isProdHost = useMemo(() => {
    if (Platform.OS !== 'web') return false;
    try {
      const host = window.location.hostname;
      return host && host !== 'localhost' && host !== '127.0.0.1';
    } catch {
      return false;
    }
  }, []);

  // Redirect users without access to coming-soon in prod when coming soon is enabled
  useEffect(() => {
    if (!isProdHost) return;
    if (comingSoonEnabled && !hasComingSoonAccess && currentScreen !== 'MainPage' && currentScreen !== 'Admin' && currentScreen !== 'Login' && currentScreen !== 'Register') {
      try {
        window.history.pushState({}, '', '/coming-soon');
        window.dispatchEvent(new Event('app:navigate'));
      } catch {
        setCurrentScreen('MainPage');
      }
    }
  }, [comingSoonEnabled, hasComingSoonAccess, currentScreen, isProdHost]);

  const navigateTo = (screenName, options = {}) => {
    // Don't navigate if already on the same screen
    if (currentScreen === screenName) {
      return;
    }

    // Handle redirect after login
    if (screenName === 'Login' && options.redirectAfterLogin) {
      setRedirectAfterLogin(options.redirectAfterLogin);
    }

    // Protect admin page - redirect non-admins (only check after user data is loaded)
    if (screenName === 'Admin' && userDataLoaded && !isAdmin) {
      console.log('Access denied: Admin page requires admin privileges');
      return;
    }
    
    // Wait for user data to load before allowing admin navigation
    if (screenName === 'Admin' && !userDataLoaded) {
      console.log('Waiting for user data to load before admin navigation...');
      return;
    }

    // Guard: SgpFlow requires a query from payload or session
    if (screenName === 'SgpFlow') {
      const hasPayloadQuery = !!options?.payload?.query;
      let hasStored = false;
      try { hasStored = !!window.sessionStorage.getItem('sgp:lastQuery'); } catch {}
      if (!hasPayloadQuery && !hasStored) {
        console.log('Blocked navigation: SgpFlow requires a query');
        return;
      }
      // Save payload query if present
      if (hasPayloadQuery) {
        try { window.sessionStorage.setItem('sgp:lastQuery', String(options.payload.query)); } catch {}
      }
    }

    // Check if current screen has an exit animation
    if (Platform.OS === 'web' && currentScreen === 'Explore' && window.exploreExitAnimation) {
      // Run exit animation before navigating
      window.exploreExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      });
    } else if (Platform.OS === 'web' && currentScreen === 'SameGameParlay' && window.sameGameParlayExitAnimation) {
      // Run exit animation before navigating
      window.sameGameParlayExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      });
    } else if (Platform.OS === 'web' && currentScreen === 'Home' && window.homeExitAnimation) {
      // Run exit animation before navigating
      window.homeExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      });
    } else if (Platform.OS === 'web' && currentScreen === 'SgpFlow' && window.sgpFlowExitAnimation) {
      // Run exit animation before navigating
      window.sgpFlowExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      });
    } else if (Platform.OS === 'web' && currentScreen === 'Profile' && window.profileExitAnimation) {
      // Run exit animation before navigating
      window.profileExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
      });
    } else if (Platform.OS === 'web' && currentScreen === 'Plans' && window.plansExitAnimation) {
      // Run exit animation before navigating
      window.plansExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
      });
    } else {
      // Normal navigation without exit animation
      setCurrentScreen(screenName);
      setRoutePayload(options?.payload || null);
      setHistory(prev => [...prev, screenName]);

      // Update URL on web
      if (Platform.OS === 'web') {
        const url = screenToUrl[screenName] || '/';
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      }
    }
  };

  const goBack = () => {
    if (history.length > 1) {
      const newHistory = [...history];
      newHistory.pop(); // Remove current screen
      const previousScreen = newHistory[newHistory.length - 1];
      
      setCurrentScreen(previousScreen);
      setHistory(newHistory);

      // Update URL on web
      if (Platform.OS === 'web') {
        const url = screenToUrl[previousScreen] || '/';
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      }
    }
  };

  const replaceScreen = (screenName) => {
    setCurrentScreen(screenName);
    setHistory(prev => [...prev.slice(0, -1), screenName]);

    // Update URL on web
    if (Platform.OS === 'web') {
      const url = screenToUrl[screenName] || '/';
      window.history.replaceState({}, '', url);
      window.dispatchEvent(new Event('app:navigate'));
    }
  };

  const handleLoginRedirect = () => {
    if (redirectAfterLogin) {
      const redirectScreen = redirectAfterLogin;
      setRedirectAfterLogin(null);
      navigateTo(redirectScreen);
    } else {
      navigateTo('Home');
    }
  };

  // Banner component
  const Banner = () => {
    if (!bannerEnabled) return null;
    
    return (
      <View style={bannerStyles.banner}>
        <Text style={bannerStyles.bannerText}>
          Site maintenance in progress{bannerStartedAt && bannerStartedAt.toDate ? ` — started ${bannerStartedAt.toDate().toLocaleString()}` : ''}
        </Text>
      </View>
    );
  };

  return (
    <NavigationContext.Provider value={{
      currentScreen,
      navigateTo,
      goBack,
      replaceScreen,
      history,
      handleLoginRedirect,
      // Direct payload setter for triggering queries without navigation
      setRoutePayloadDirect: setRoutePayload,
      // Admin and maintenance state
      authUser,
      userType,
      isAdmin,
      alphaAccess,
      hasComingSoonAccess,
      comingSoonEnabled,
      bannerEnabled,
      bannerStartedAt,
      communityModalEnabled,
      communityRedirectUrl,
      Banner,
      routePayload
    }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}

const bannerStyles = {
  banner: { 
    backgroundColor: '#1f2937', 
    paddingVertical: 6, 
    paddingHorizontal: 12, 
    borderBottomWidth: 1, 
    borderBottomColor: 'rgba(255,255,255,0.08)' 
  },
  bannerText: { 
    color: 'rgba(255,255,255,0.9)', 
    textAlign: 'center' 
  }
};
